import { Box, IconButton, Stack, styled, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as SendIcon } from "images/send-message-icon.svg";
import { Message } from "./Message";

const MESSAGE_RELOAD_INTERVAL = 5000;

const Title = styled(Box)`
  font-size: 22px;
  font-weight: bold;
  color: black;
  text-align: left;
`;

export const List = styled(Stack)`
  justify-content: space-between;
`;

const NewMessageForm = styled(Stack)`
  width: 100%;
  align-self: center;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  padding: 10px 20px;
  flex: 0 0 auto;
  height: fit-content;
  color: white;
  background-color: black;
`;

const Container = styled(Stack)`
  flex: 1 1 auto;

  width: 100%;

  border: thin solid #d5dce6;
  border-radius: 13px;
  background-color: #f8f9fb;
  overflow-y: hidden;
`;

const SendButton = styled(IconButton)`
  background-color: #225da4;
  &:hover {
    background-color: #1a4a8c;
  }
  border-radius: 10px;
`;

const MessagesContainer = styled(Stack)`
  padding: 10px 0 20px 0;
  flex: 1 1 auto;

  width: 100%;

  min-width: 240px;
  min-height: 260px;

  justify-content: flex-start;
  align-items: center;

  overflow-y: auto;
  overflow-x: hidden;

  background-color: #fafafa;
`;

export const Messages = ({
  referral,
  admins,
  marketers,
  schedulers,
  originator,
  showError,
}) => {
  const [isSending, setIsSending] = useState(false);
  const messagesContainer = useRef(null);
  const inputRef = useRef(null);
  const [prevMessages, setPrevMessages] = useState(null);
  const [messages, setMessages] = useState(null);

  const { originatorId, originatorName, createdAt } = referral ?? {};

  const sendNewMessage = async () => {
    if (isSending) return;
    setIsSending(true);
    const referralId = referral?.id;
    if (!referralId) return;

    const message = inputRef.current.value;
    if (!message) return;

    const data = {
      message,
      referralId,
      dashboardAccessToken: originator?.dashboardAccessToken,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        showError(result?.message || result?.error || "Something went wrong");
        return;
      }

      setMessages((prevMessages) => [...(prevMessages ?? []), result]);
      inputRef.current.value = "";
    } catch (err) {
      showError(err?.error ?? err?.message ?? "Something went wrong");
    } finally {
      setIsSending(false);
    }
  };

  const loadData = async (referral) => {
    const referralId = referral?.id;
    if (!referralId) return;

    try {
      const data = {
        referralId,
        dashboardAccessToken: originator?.dashboardAccessToken,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/messages/all-messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      const result = await response.json();
      if (response.status !== 200 || result?.error || result?.message) {
        showError(result?.message || result?.error || "Something went wrong");
        setMessages([]);
        return;
      }

      setMessages(result);
    } catch (err) {
      showError(err?.error ?? err?.message ?? "Something went wrong");
      setMessages([]);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    if (!messagesContainer.current) return;
    const height = messagesContainer.current.scrollHeight;

    messagesContainer.current.scrollTop = height;
  };

  const isSendingDisabled =
    isSending || ["Completed", "Canceled"].includes(referral?.status);

  useEffect(() => {
    scrollToBottom();
    loadData(referral);

    const interval = setInterval(() => {
      loadData(referral);
    }, MESSAGE_RELOAD_INTERVAL);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral]);

  useEffect(() => {
    if (prevMessages?.length === messages?.length) return;
    scrollToBottom();
    setPrevMessages(messages);
  }, [messages, prevMessages]);

  const question = referral?.question;

  return (
    <Stack
      flex={1}
      justifyContent={"flex-start"}
      mt={1}
      alignSelf={"stretch"}
      gap={1}
      width={"100%"}
      maxWidth={"640px"}
      maxHeight={"480px"}
    >
      <Title>Messages</Title>
      <Container>
        <MessagesContainer ref={messagesContainer}>
          {!!question && (
            <Message
              key={question.id}
              createdAt={createdAt}
              creatorId={originatorId}
              message={question}
              originatorId={originatorId}
              originatorName={originatorName}
              admins={admins}
              marketers={marketers}
              schedulers={schedulers}
              originator={originator}
            />
          )}
          {messages === null && (
            <Stack flex={1} justifyContent={"center"} alignItems={"center"}>
              Loading...
            </Stack>
          )}
          {messages?.length === 0 && !question && (
            <Stack flex={1} justifyContent={"center"} alignItems={"center"}>
              No messages
            </Stack>
          )}
          {messages?.map(({ id, createdAt, creatorId, message }) => (
            <Message
              key={id}
              {...{
                createdAt,
                creatorId,
                message,
                originatorId,
                originatorName,
                admins,
                marketers,
                schedulers,
                originator,
              }}
            />
          ))}
        </MessagesContainer>
        <NewMessageForm>
          <TextField
            placeholder="Type your message here"
            multiline
            fullWidth
            variant="outlined"
            size="small"
            inputRef={inputRef}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            disabled={isSendingDisabled}
            sx={{
              width: "99%",
            }}
          />
          <SendButton disabled={isSendingDisabled} onClick={sendNewMessage}>
            <SendIcon />
          </SendButton>
        </NewMessageForm>
      </Container>
    </Stack>
  );
};
